import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  extractIdsFromData,
  toggleSelectionUtil,
  toggleAllUtil,
  isAllSelectedUtil,
  isPartiallySelectedUtil,
} from './selectionState.utils';

export interface UseSelectionStateReturn {
  currentSelections: string[];
  isDirty: boolean;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
  isSelected: (id: string) => boolean;
  toggleSelection: (id: string) => void;
  toggleAll: () => void;
  resetSelections: () => void;
}

export function useSelectionState<TData = unknown>(
  data: TData[],
  idKey: keyof TData,
  initialSelections: string[]
): UseSelectionStateReturn {
  const [currentSelections, setCurrentSelections] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setCurrentSelections(initialSelections);
    setIsDirty(false);
  }, [initialSelections]);

  const isSelected = useCallback(
    (id: string): boolean => currentSelections.includes(id),
    [currentSelections]
  );

  const toggleSelection = useCallback((id: string): void => {
    setCurrentSelections((prev) => toggleSelectionUtil(prev, id));
    setIsDirty(true);
  }, []);

  const toggleAll = useCallback((): void => {
    const visibleIds = extractIdsFromData(data, idKey);
    setCurrentSelections((prev) => toggleAllUtil(prev, visibleIds));
    setIsDirty(true);
  }, [data, idKey]);

  const resetSelections = useCallback((): void => {
    setCurrentSelections(initialSelections);
    setIsDirty(false);
  }, [initialSelections]);

  const visibleIds = useMemo(
    () => extractIdsFromData(data, idKey),
    [data, idKey]
  );

  const isAllSelected = useMemo(
    () => isAllSelectedUtil(currentSelections, visibleIds),
    [currentSelections, visibleIds]
  );

  const isPartiallySelected = useMemo(
    () => isPartiallySelectedUtil(currentSelections, visibleIds),
    [currentSelections, visibleIds]
  );

  return {
    currentSelections,
    isDirty,
    isAllSelected,
    isPartiallySelected,
    isSelected,
    toggleSelection,
    toggleAll,
    resetSelections,
  };
}
